
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';

interface MaintenanceSummaryBarProps {
  alertCounts: {
    total: number;
    critical: number;
    medium: number;
    low: number;
  };
  onFilterChange: (filterType: 'assetType' | 'priority' | 'facility', value: string | null) => void;
  selectedAssetType: string | null;
  selectedPriority: string | null;
  selectedFacility: string | null;
}

const MaintenanceSummaryBar: React.FC<MaintenanceSummaryBarProps> = ({
  alertCounts,
  onFilterChange,
  selectedAssetType,
  selectedPriority,
  selectedFacility
}) => {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Total Alerts:</span>
          <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
            {alertCounts.total}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-medium">Critical:</span>
          <Badge variant="outline" className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
            {alertCounts.critical}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium">Medium:</span>
          <Badge variant="outline" className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
            {alertCounts.medium}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-emerald-500" />
          <span className="text-sm font-medium">Low:</span>
          <Badge variant="outline" className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
            {alertCounts.low}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-2 w-full sm:w-auto">
        <Select onValueChange={(value) => onFilterChange('assetType', value === 'all' ? null : value)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Asset Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="chiller">Chiller</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
            <SelectItem value="smartMeter">Smart Meter</SelectItem>
            <SelectItem value="loadController">Load Controller</SelectItem>
            <SelectItem value="gateway">IoT Gateway</SelectItem>
            <SelectItem value="transformer">Transformer</SelectItem>
            <SelectItem value="sensor">Sensor</SelectItem>
          </SelectContent>
        </Select>

        <Select onValueChange={(value) => onFilterChange('priority', value === 'all' ? null : value)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select onValueChange={(value) => onFilterChange('facility', value === 'all' ? null : value)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Facility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Facilities</SelectItem>
            <SelectItem value="plant1">Plant 1</SelectItem>
            <SelectItem value="plant2">Plant 2</SelectItem>
            <SelectItem value="headquarters">Headquarters</SelectItem>
            <SelectItem value="datacenter">Data Center</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default MaintenanceSummaryBar;
